import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import ActiveSchoolSwitcher from '../components/ActiveSchoolSwitcher';
import { getApiBaseUrl } from '../apiBase';
import { getActiveSchoolId } from '../utils/activeSchool';
import { useTranslation } from 'react-i18next';


const API = getApiBaseUrl();

function assetUrl(path) {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const base = API.replace(/\/api\/?$/, '');
    return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

const emptyForm = {
    name: '',
    description: '',
    characteristics: '',
    price: '',
    quantity: '1',
    type: 'rent'
};

export default function InventoryManagePage() {
    const { t } = useTranslation(['inventoryManage', 'common', 'nav', 'messages', 'errors']);
    const [schoolId, setSchoolId] = useState(getActiveSchoolId());
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const fileRef = useRef(null);
    const [uploadTargetId, setUploadTargetId] = useState(null);

    const token = localStorage.getItem('token');
    const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

    useEffect(() => {
        const onStorage = () => setSchoolId(getActiveSchoolId());
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    useEffect(() => { if (schoolId) load(); }, [schoolId]);

    async function load() {
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/inventory/manage', { params: { schoolId } });
            setItems(res.data || []);
        } catch (err) {
            setError(err.response?.data?.message || t('inventoryManage:loadError'));
        }
        setLoading(false);
    }

    async function handleSave(e) {
        e.preventDefault();
        if (!form.name.trim()) return;
        setSaving(true);
        setError('');
        const body = {
            name: form.name,
            description: form.description,
            characteristics: form.characteristics,
            type: form.type,
            price: parseFloat(form.price) || 0,
            quantity: parseInt(form.quantity, 10) || 1
        };
        try {
            if (editingId) {
                await api.put(`/inventory/${editingId}`, body);
            } else {
                await api.post('/inventory', body, { params: { schoolId } });
            }
            setForm(emptyForm);
            setEditingId(null);
            await load();
        } catch (err) {
            setError(err.response?.data?.message || t('inventoryManage:saveError'));
        }
        setSaving(false);
    }

    function startEdit(item) {
        setEditingId(item.id);
        setForm({
            name: item.name,
            description: item.description || '',
            characteristics: item.characteristics || '',
            price: String(item.price),
            quantity: String(item.totalQuantity),
            type: item.type || 'rent'
        });
    }

    async function handleDeactivate(id) {
        if (!window.confirm(t('inventoryManage:deactivateConfirm'))) return;
        try {
            await api.delete(`/inventory/${id}`);
            load();
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
    }

    async function handleImageUpload(e) {
        const file = e.target.files?.[0];
        const id = uploadTargetId;
        if (!file || !id) return;
        const fd = new FormData();
        fd.append('file', file);
        try {
            await api.post(`/inventory/${id}/image`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            load();
        } catch (err) {
            alert(err.response?.data?.message || t('inventoryManage:photoError'));
        }
        setUploadTargetId(null);
        e.target.value = '';
    }

    const inputStyle = {
        width: '100%', padding: '11px 13px', borderRadius: '10px',
        border: '1.5px solid var(--sp-gray-200)', fontSize: '14px', boxSizing: 'border-box'
    };

    return (
        <OwnerPageLayout
            title={t('inventoryManage:title')}
            subtitle={t('inventoryManage:subtitle')}
            maxWidth={960}
        >
            <ActiveSchoolSwitcher onSchoolChange={setSchoolId} />

            {!schoolId && (
                <p style={{ color: 'var(--sp-error)' }}>{t('inventoryManage:pickSchool')}</p>
            )}

            <div className="sp-panel" style={{ marginBottom: 24 }}>
                <h3 className="sp-panel__title" style={{ marginBottom: 16 }}>
                    {editingId ? t('inventoryManage:editItem') : t('inventoryManage:newItem')}
                </h3>
                <form onSubmit={handleSave}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label className="sp-field__label">{t('inventoryManage:nameLabel')}</label>
                            <input className="sp-input" style={inputStyle} value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })} required />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label className="sp-field__label">{t('inventoryManage:charsLabel')}</label>
                            <input className="sp-input" style={inputStyle} value={form.characteristics}
                                onChange={e => setForm({ ...form, characteristics: e.target.value })}
                                placeholder={t('inventoryManage:charsPlaceholder')} />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label className="sp-field__label">{t('inventoryManage:descLabel')}</label>
                            <textarea className="sp-input" style={{ ...inputStyle, minHeight: '72px' }} value={form.description}
                                onChange={e => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div>
                            <label className="sp-field__label">{t('inventoryManage:priceLabel')}</label>
                            <input className="sp-input" style={inputStyle} type="number" min="0" step="0.01" value={form.price}
                                onChange={e => setForm({ ...form, price: e.target.value })} required />
                        </div>
                        <div>
                            <label className="sp-field__label">{t('inventoryManage:qtyLabel')}</label>
                            <input className="sp-input" style={inputStyle} type="number" min="0" value={form.quantity}
                                onChange={e => setForm({ ...form, quantity: e.target.value })} />
                        </div>
                    </div>
                    {error && <p style={{ color: 'var(--sp-error)', fontSize: '13px' }}>{error}</p>}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                        <button type="submit" disabled={saving || !schoolId} className="sp-btn sp-btn--primary">
                            {saving ? t('inventoryManage:saving') : editingId ? t('common:save') : t('common:add')}
                        </button>
                        {editingId && (
                            <button type="button" className="sp-btn sp-btn--ghost"
                                onClick={() => { setEditingId(null); setForm(emptyForm); }}>
                                {t('common:cancel')}
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={handleImageUpload} />

            <h3 className="sp-panel__title" style={{ marginBottom: 12 }}>{t('inventoryManage:catalogTitle', { count: items.length })}</h3>
            {loading ? <p className="sp-loading-page">{t('inventoryManage:loading')}</p> : items.length === 0 ? (
                <p style={{ color: 'var(--sp-gray-400)' }}>{t('inventoryManage:empty')}</p>
            ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                    {items.map(item => (
                        <div key={item.id} className="sp-panel" style={{
                            opacity: item.isActive ? 1 : 0.6,
                            display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: 0
                        }}>
                            <div style={{
                                width: '80px', height: '80px', borderRadius: '10px',
                                background: 'var(--sp-primary-bg)', overflow: 'hidden', flexShrink: 0
                            }}>
                                {item.imageUrl ? (
                                    <img src={assetUrl(item.imageUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ lineHeight: '80px', textAlign: 'center', fontSize: '28px' }}>🛼</div>
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '700' }}>{item.name}</div>
                                {item.characteristics && (
                                    <div style={{ fontSize: '13px', color: 'var(--sp-gray-600)' }}>{item.characteristics}</div>
                                )}
                                <div style={{ fontSize: '14px', marginTop: '6px' }}>
                                    {t('inventoryManage:itemLine', { price: item.price, available: item.availableQuantity, total: item.totalQuantity })}
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <button type="button" title={t('common:edit')} onClick={() => startEdit(item)} className="sp-btn sp-btn--ghost" style={btnSm}>✏️</button>
                                <button type="button" title={t('inventoryManage:photoTitle')} onClick={() => { setUploadTargetId(item.id); fileRef.current?.click(); }} className="sp-btn sp-btn--ghost" style={btnSm}>📷</button>
                                {item.isActive && (
                                    <button type="button" title={t('inventoryManage:deactivateTitle')} onClick={() => handleDeactivate(item.id)} className="sp-btn sp-btn--ghost" style={{ ...btnSm, color: 'var(--sp-error)' }}>🗑</button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </OwnerPageLayout>
    );
}

const btnSm = {
    padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--sp-gray-200)',
    background: '#fff', cursor: 'pointer', fontSize: '13px'
};
